// modules/lib/src/manifest.ts — LIB's nav contribution, one export per portal
// surface it serves (ADR: "Adding a screen to the staff portal = adding a nav
// entry to your module's manifest. The portal diff is zero lines.").
// Same routes + permission strings that used to live embedded in the admin
// portal's home.tsx card directory (makeLibCatalogueRoutes and siblings own
// the actual routing — this is only the menu view of it).
//
// EVERY item carries an icon, not just the first of each section: the sidebar
// aligns labels off the icon slot, so a partial set left the list visually
// ragged and made scanning harder, which is the one job a sidebar has.
import {
  Activity,
  ArrowLeftRight,
  BarChart3,
  BookMarked,
  BookOpen,
  Bookmark,
  Building2,
  CalendarDays,
  CircleDollarSign,
  ClipboardCheck,
  Compass,
  CreditCard,
  FileUp,
  Globe,
  HardDrive,
  History,
  LayoutDashboard,
  Library,
  ListPlus,
  Mail,
  Map,
  MapPin,
  MonitorSmartphone,
  Network,
  Newspaper,
  PackageCheck,
  PackageX,
  PenLine,
  Receipt,
  RefreshCw,
  Scale,
  Settings,
  Send,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Tablet,
  Tags,
  TrendingUp,
  Truck,
  Undo2,
  Users,
  Wallet,
} from "lucide-solid";
import type { ModuleManifest } from "@erp/auth-kit/nav";

/** apps/portals/admin — the full operator console. */
export const libAdminNav: ModuleManifest = {
  id: "lib",
  module: "lib",
  label: "Library",
  to: "/library",
  icon: Library,
  // ONE section of collapsible GROUPS (restructured 2026-09-02): the old flat
  // sections became parent items with `children`, which is what turns on the
  // sidebar's second nav level — collapsible parents, the 1px hierarchy rail,
  // active-child auto-expand. 59 permanently-visible rows became ~10 resting
  // ones. Parents carry NO perm on purpose: buildNav drops a parent whose
  // every child is denied, so visibility still comes entirely from the leaves.
  // A parent's `to` is its route PREFIX (matching/breadcrumbs only — a parent
  // with children renders as a toggle, not a link); where a landing child
  // shares it (Patrons ▸ Roster), the matchers' child-first tie-break lights
  // the child.
  sections: [
    {
      id: "lib-nav",
      label: "Library management",
      items: [
        {
          id: "lib-catalogue",
          // "Titles", not "Books" and emphatically not "Monographs": the table
          // holds maps, scores, manuscripts and government documents now, and
          // "monograph" is cataloguer jargon that no shipping ILS puts in a
          // menu (Koha "Catalog", Alma "Resources"/"Titles"). Material type is a
          // FACET inside the screen — never a menu item per type, or nine types
          // become nine menu entries and the taxonomy is hard-coded into
          // navigation.
          label: "Catalogue",
          to: "/library/catalogue",
          icon: BookOpen,
          children: [
            { id: "lib-catalogue-books", label: "Titles", to: "/library/catalogue/books", icon: BookOpen, perm: "lib:catalog.read" },
            { id: "lib-catalogue-bulk", label: "Bulk add", to: "/library/catalogue/bulk-add", icon: ListPlus, perm: "lib:catalog" },
            { id: "lib-catalogue-marc", label: "MARC import", to: "/library/catalogue/marc-import", icon: FileUp, perm: "lib:catalog" },
            { id: "lib-catalogue-authors", label: "Authors", to: "/library/catalogue/authors", icon: PenLine, perm: "lib:catalog.read" },
            { id: "lib-catalogue-publishers", label: "Publishers", to: "/library/catalogue/publishers", icon: Building2, perm: "lib:catalog.read" },
            { id: "lib-catalogue-subjects", label: "Subjects & Tags", to: "/library/catalogue/subjects", icon: Tags, perm: "lib:catalog.read" },
            // HIDDEN leaves (audit UX-18): create/edit pages and utility routes
            // have no menu row; these give the breadcrumb the page's own name
            // while the sidebar keeps the nearest visible row lit (Titles for a
            // book's form). `$param` segments match any one segment.
            { id: "lib-catalogue-books-new", label: "Add book", to: "/library/catalogue/books/new", perm: "lib:catalog", hidden: true },
            { id: "lib-catalogue-books-edit", label: "Edit book", to: "/library/catalogue/books/$bookId/edit", perm: "lib:catalog", hidden: true },
          ],
        },
        {
          id: "lib-patrons",
          label: "Patrons",
          to: "/library/patrons",
          icon: Users,
          children: [
            { id: "lib-patrons-roster", label: "Roster", to: "/library/patrons", icon: Users, perm: "lib:patrons.read" },
            // The WRITE cap, unlike its siblings: a reader may see the roster
            // and may not add to it. /library/patrons/new deliberately has no
            // entry (it is reached from the roster header), but an invitation
            // is a task a librarian sets out to do rather than one they arrive
            // at while looking at somebody.
            { id: "lib-patrons-invite", label: "Invite a patron", to: "/library/patrons/invite", icon: Send, perm: "lib:patrons" },
            { id: "lib-patrons-groups", label: "Groups & policies", to: "/library/patrons/groups", icon: ShieldCheck, perm: "lib:patrons.read" },
            { id: "lib-patrons-cards", label: "Library cards", to: "/library/patrons/cards", icon: CreditCard, perm: "lib:patrons.read" },
            { id: "lib-patrons-parental", label: "Parental controls", to: "/library/patrons/parental", icon: ShieldCheck, perm: "lib:patrons.read" },
            { id: "lib-patrons-holds", label: "Holds", to: "/library/patrons/holds", icon: Bookmark, perm: "lib:desk" },
            { id: "lib-patrons-fines", label: "Fines", to: "/library/patrons/fines", icon: CircleDollarSign, perm: "lib:patrons.read" },
            { id: "lib-patrons-new", label: "Add patron", to: "/library/patrons/new", perm: "lib:patrons", hidden: true },
            { id: "lib-patrons-edit", label: "Edit patron", to: "/library/patrons/$patronId/edit", perm: "lib:patrons", hidden: true },
          ],
        },
        {
          id: "lib-memberships",
          label: "Memberships",
          to: "/library/memberships",
          icon: CreditCard,
          children: [
            { id: "lib-mem-plans", label: "Plans", to: "/library/memberships/plans", icon: CreditCard, perm: "lib:patrons.read" },
            { id: "lib-mem-dues", label: "Member dues", to: "/library/memberships/dues", icon: Receipt, perm: "lib:patrons.read" },
            { id: "lib-mem-payments", label: "Payments", to: "/library/memberships/payments", icon: Wallet, perm: "lib:reports" },
            { id: "lib-mem-revenue", label: "Revenue", to: "/library/memberships/revenue", icon: TrendingUp, perm: "lib:reports" },
          ],
        },
        {
          id: "lib-acquisitions",
          label: "Acquisitions",
          to: "/library/acquisitions",
          icon: ShoppingCart,
          children: [
            { id: "lib-acq-pos", label: "Purchase orders", to: "/library/acquisitions/purchase-orders", icon: ShoppingCart, perm: "lib:acq" },
            { id: "lib-acq-receiving", label: "Receiving", to: "/library/acquisitions/receiving", icon: PackageCheck, perm: "lib:acq" },
            { id: "lib-acq-vendors", label: "Vendors", to: "/library/acquisitions/vendors", icon: Truck, perm: "lib:acq" },
            { id: "lib-acq-budget", label: "Budget lines", to: "/library/acquisitions/budget-lines", icon: Wallet, perm: "lib:acq" },
            { id: "lib-acq-pos-new", label: "New purchase order", to: "/library/acquisitions/purchase-orders/new", perm: "lib:acq", hidden: true },
          ],
        },
        {
          id: "lib-digital",
          label: "Digital resources",
          to: "/library/digital",
          icon: Tablet,
          children: [
            { id: "lib-digital-ebooks", label: "eBooks", to: "/library/digital/ebooks", icon: Tablet, perm: "lib:catalog.read" },
            { id: "lib-digital-sessions", label: "Reading sessions", to: "/library/digital/sessions", icon: Activity, perm: "lib:catalog.read" },
            { id: "lib-digital-storage", label: "Storage", to: "/library/digital/storage", icon: HardDrive, perm: "lib:catalog.read" },
            { id: "lib-digital-ebooks-new", label: "Upload eBook", to: "/library/digital/ebooks/new", perm: "lib:catalog", hidden: true },
          ],
        },
        {
          id: "lib-locations",
          label: "Locations",
          to: "/library/locations",
          icon: MapPin,
          children: [
            { id: "lib-locations-libraries", label: "Libraries", to: "/library/locations/libraries", icon: MapPin, perm: "lib:admin" },
            { id: "lib-locations-branches", label: "Branches & zones", to: "/library/locations/branches", icon: Network, perm: "lib:admin" },
            { id: "lib-locations-map", label: "Map Builder", to: "/library/locations/map", icon: Map, perm: "lib:admin" },
            // The three registries under Branches & zones are reached by
            // drilling down, never from the menu — hidden, so their crumb reads
            // "Locations › Zones" rather than the group's own name.
            { id: "lib-locations-zones", label: "Zones", to: "/library/locations/zones", perm: "lib:admin", hidden: true },
            { id: "lib-locations-stacks", label: "Stacks", to: "/library/locations/stacks", perm: "lib:admin", hidden: true },
            { id: "lib-locations-shelves", label: "Shelves", to: "/library/locations/shelves", perm: "lib:admin", hidden: true },
          ],
        },
        {
          id: "lib-operations",
          label: "Operations",
          to: "/library/operations",
          icon: ClipboardCheck,
          children: [
            { id: "lib-ops-serials", label: "Serials", to: "/library/operations/serials", icon: Newspaper, perm: "lib:catalog.read" },
            { id: "lib-ops-transit", label: "Transit", to: "/library/operations/transit", icon: ArrowLeftRight, perm: "lib:catalog.read" },
            { id: "lib-ops-stocktake", label: "Stocktake", to: "/library/operations/stocktake", icon: ClipboardCheck, perm: "lib:catalog.read" },
            { id: "lib-ops-lost", label: "Lost & damaged", to: "/library/operations/lost-damaged", icon: PackageX, perm: "lib:desk" },
            { id: "lib-ops-txns", label: "Transaction history", to: "/library/operations/transactions", icon: History, perm: "lib:catalog.read" },
            { id: "lib-ops-reports", label: "Storage & usage", to: "/library/operations/reports", icon: BarChart3, perm: "lib:catalog.read" },
            { id: "lib-ops-audit", label: "Audit log", to: "/library/operations/audit", icon: ShieldCheck, perm: "lib:admin" },
            { id: "lib-ops-serials-new", label: "New subscription", to: "/library/operations/serials/new", perm: "lib:catalog", hidden: true },
          ],
        },
        {
          id: "lib-reports",
          label: "Reports",
          to: "/library/reports",
          icon: BarChart3,
          children: [
            { id: "lib-reports-overview", label: "Overview", to: "/library/reports", icon: LayoutDashboard, perm: "lib:reports" },
            { id: "lib-reports-circulation", label: "Circulation", to: "/library/reports/circulation", icon: ArrowLeftRight, perm: "lib:reports" },
            { id: "lib-reports-catalogue", label: "Catalogue", to: "/library/reports/catalogue", icon: BookOpen, perm: "lib:reports" },
            { id: "lib-reports-digital", label: "Digital usage", to: "/library/reports/digital", icon: MonitorSmartphone, perm: "lib:reports" },
            { id: "lib-reports-fines", label: "Fine collection", to: "/library/reports/fines", icon: CircleDollarSign, perm: "lib:reports" },
            { id: "lib-reports-members", label: "Member activity", to: "/library/reports/members", icon: Users, perm: "lib:reports" },
            { id: "lib-reports-acquisitions", label: "Acquisitions", to: "/library/reports/acquisitions", icon: ShoppingCart, perm: "lib:reports" },
          ],
        },
        {
          id: "lib-settings",
          label: "Library settings",
          to: "/library/settings",
          icon: Settings,
          children: [
            { id: "lib-settings-institution", label: "Institution profile", to: "/library/settings/institution", icon: Building2, perm: "lib:admin" },
            { id: "lib-settings-opac", label: "OPAC & Discovery", to: "/library/settings", icon: Globe, perm: "lib:admin" },
            { id: "lib-settings-policies", label: "Circulation policies", to: "/library/settings/policies", icon: Scale, perm: "lib:patrons.read" },
            { id: "lib-settings-calendar", label: "Academic calendar", to: "/library/settings/calendar", icon: CalendarDays, perm: "lib:patrons.read" },
            { id: "lib-settings-notifications", label: "Email notifications", to: "/library/settings/notifications", icon: Mail, perm: "lib:admin" },
          ],
        },
      ],
    },
  ],
};

/**
 * apps/portals/student — the OPAC. Two halves with DIFFERENT gates, exactly as
 * that portal's router.tsx documents: the catalogue is PUBLIC (no `perm`, so
 * buildNav keeps it for anonymous visitors — the OPAC is browsable without an
 * account and the server decides anonymous-vs-gated per request), while the
 * member area requires lib:opac and disappears entirely when signed out.
 *
 * NOTE the two roots (`/catalogue`, `/account`): this is why the active module
 * is resolved from a module's ITEMS rather than one declared path prefix.
 */
export const libStudentNav: ModuleManifest = {
  id: "opac",
  // Three surfaces (lib / opac / desk), ONE install module: all vanish
  // together on an install without lib (§13).
  module: "lib",
  label: "Library",
  to: "/catalogue",
  icon: Library,
  sections: [
    {
      id: "opac-catalogue",
      label: "Catalogue",
      items: [
        { id: "opac-browse", label: "Browse", to: "/catalogue", icon: Compass },
        { id: "opac-books", label: "Search the catalogue", to: "/catalogue/books", icon: BookOpen },
        { id: "opac-ebooks", label: "Ebooks", to: "/catalogue/ebooks", icon: Tablet },
      ],
    },
    {
      id: "opac-account",
      label: "My account",
      items: [
        { id: "opac-account-overview", label: "Overview", to: "/account", icon: LayoutDashboard, perm: "lib:opac" },
        { id: "opac-account-loans", label: "Loans", to: "/account/loans", icon: BookMarked, perm: "lib:opac" },
        { id: "opac-account-holds", label: "Holds", to: "/account/holds", icon: BookOpen, perm: "lib:opac" },
        { id: "opac-account-fines", label: "Fines", to: "/account/fines", icon: CircleDollarSign, perm: "lib:opac" },
      ],
    },
  ],
};

/**
 * apps/portals/staff — the circulation desk. Deliberately small: the desk is a
 * focused workflow surface, not a second admin console.
 */
export const libStaffNav: ModuleManifest = {
  id: "desk",
  module: "lib",
  label: "Circulation desk",
  to: "/library/desk/issue",
  icon: Smartphone,
  sections: [
    {
      id: "lib-desk",
      label: "Circulation",
      items: [
        { id: "lib-desk-issue", label: "Issue", to: "/library/desk/issue", icon: BookMarked, perm: "lib:desk" },
        { id: "lib-desk-return", label: "Return", to: "/library/desk/return", icon: Undo2, perm: "lib:desk" },
        { id: "lib-desk-renewals", label: "Renewals", to: "/library/desk/renewals", icon: RefreshCw, perm: "lib:desk" },
        { id: "lib-desk-devices", label: "Connect a phone", to: "/library/desk/devices", icon: Smartphone, perm: "lib:desk" },
      ],
    },
  ],
};
